import {useCallback, useState} from 'react';

import {addToast} from '@albert-io/atomic';
import {normalizeError} from 'lib/normalizeError';

import {fetchAssignmentDeleteScope} from '../assignmentDeleteScope';
import type {FetchedAssignmentDeleteScope} from '../assignmentDeleteScope';

interface AssignmentDeleteScopeRead {
  /** The last successful read; `null` before one lands, and cleared again if one fails. */
  data: FetchedAssignmentDeleteScope | null;
  isLoading: boolean;
  /**
   * Reads the scope and hands it back, so a caller can decide on it in the same tick that it asked —
   * `data` only catches up on the next render. Resolves `null` when the read failed, which it has
   * already surfaced as a toast.
   */
  read: (assignmentId: string) => Promise<FetchedAssignmentDeleteScope | null>;
}

/**
 * The read half of a cross-class delete scope, as component state.
 *
 * Deliberately reads again on every call rather than reusing what it already holds: the delete it
 * feeds is irreversible, and "removing my classes would leave none on this assignment" is only true
 * as of the read — a count kept from an earlier open can turn a co-teacher's class into collateral.
 */
export const useAssignmentDeleteScope = (): AssignmentDeleteScopeRead => {
  const [data, setData] = useState<FetchedAssignmentDeleteScope | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const read = useCallback(async (assignmentId: string) => {
    setIsLoading(true);
    try {
      const scope = await fetchAssignmentDeleteScope(assignmentId);
      setData(scope);
      return scope;
    } catch (error) {
      setData(null);
      addToast({
        color: 'negative',
        title: "Couldn't prepare the delete",
        message: normalizeError(error).message
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {data, isLoading, read};
};
